import { isValidMimeType, useDeleteDocument, useFindDocuments, useUploadDocument } from "@api";
import { DocumentTypes } from "@bbj-types/document/enum";
import type { ISocialOffer } from "@bbj-types/offer";
import type { ObjectId } from "@bbj-types/utils";
import axios, { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { TModalSocialOfferFormResolver } from "./resolver";

export interface IDocumentsActionsProgress {
  currentStep: number;
  totalSteps: number;
}

export const useModalSocialOfferDocuments = (
  hookForm: UseFormReturn<TModalSocialOfferFormResolver>,
  open: boolean,
  socialOffer: ISocialOffer | null
) => {
  const [{ loading: loadingFindDocuments }, findDocuments] = useFindDocuments();
  const [, uploadUrlDocument] = useUploadDocument();
  const [, deleteDocument] = useDeleteDocument();
  const [progress, setProgress] = useState<IDocumentsActionsProgress | null>(null);

  const findSocialOfferDocuments = async (documents: ObjectId[]) => {
    try {
      const response = await findDocuments({ ids: documents });
      if (response.status === 200) {
        hookForm.setValue(
          "documents",
          response.data.map((document) => ({ info: document }))
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const splitSocialOfferDocuments = (documents: TModalSocialOfferFormResolver["documents"]) => {
    const toCreate: File[] = [];
    const toKeep: ObjectId[] = [];

    for (const document of documents) {
      if (!document.info && document.file) {
        toCreate.push(document.file);
      } else if (document.info) {
        toKeep.push(document.info._id);
      }
    }

    const toDelete = socialOffer?.documents?.filter((documentId) => !toKeep.includes(documentId)) ?? [];

    return { toCreate, toDelete, toKeep };
  };

  const handleSocialOfferDocumentsDelete = async (documents: ObjectId[]) => {
    const failedToDelete: ObjectId[] = [];
    for (const documentId of documents) {
      try {
        await deleteDocument(documentId);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status !== 404) {
          failedToDelete.push(documentId);
        }
      }
      setProgress((progress) => progress && { ...progress, currentStep: ++progress.currentStep });
    }
    return failedToDelete;
  };

  const handleSocialOfferDocumentsCreate = async (files: File[]): Promise<ObjectId[]> => {
    const documents: ObjectId[] = [];
    for (const file of files) {
      try {
        const type = file.type;
        if (isValidMimeType(type)) {
          const responseUploadUrl = await uploadUrlDocument(file.name, DocumentTypes.SOCIAL_OFFER, type, true);
          await axios.postForm(responseUploadUrl.data.url, {
            ...responseUploadUrl.data.fields,
            file,
          });
          documents.push(responseUploadUrl.data.document._id);
        }
      } catch (error) {
        // Nothing on error
      }
      setProgress((progress) => progress && { ...progress, currentStep: progress.currentStep + 1 });
    }
    return documents;
  };

  const handleSocialOfferDocuments = async (documents: TModalSocialOfferFormResolver["documents"]) => {
    const { toCreate, toDelete, toKeep } = splitSocialOfferDocuments(documents);
    const totalSteps = toCreate.length + toDelete.length;
    setProgress({ currentStep: 0, totalSteps });
    const failedToDelete = await handleSocialOfferDocumentsDelete(toDelete);
    const createdDocuments = await handleSocialOfferDocumentsCreate(toCreate);
    setProgress(null);
    if (failedToDelete.length + (toCreate.length - createdDocuments.length) === totalSteps && totalSteps !== 0) {
      const error = new Error("All files actions failed");
      error.name = "DOCUMENTS_ACTIONS_ERROR";
      throw error;
    }
    return [...createdDocuments, ...toKeep, ...failedToDelete];
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (open && socialOffer?.documents?.length) {
      void findSocialOfferDocuments(socialOffer.documents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, socialOffer]);

  return { documentsProgress: progress, loadingFindDocuments, handleSocialOfferDocuments };
};
